const SCRIPT_ID = "google-translate-script";
const WIDGET_CONTAINER_ID = "google_translate_element";
const COOKIE_NAME = "googtrans";

export const PAGE_LANGUAGE = "en";

const COMBO_SELECTOR = ".goog-te-combo";

/** Gap between dropping the translation and asking for it back. See retranslate(). */
const RESTORE_DELAY_MS = 250;

/**
 * Google's <select> has no option for the page language — it is the empty
 * "off" entry, which is what restores the original text. Assigning "en" would
 * silently coerce to this same empty string, so say it outright.
 */
const RESTORE_VALUE = "";

/**
 * Native names for the languages NYCPS is required to support. Google labels
 * its options in English; overriding these means a student who reads only one
 * of them can still find it, and can find their way back out of a language
 * they picked by accident.
 */
const NATIVE_LABELS = {
  en: "English",
  es: "Español",
  "zh-CN": "中文",
  ar: "العربية",
  bn: "বাংলা",
  fr: "Français",
  ht: "Kreyòl Ayisyen",
  ko: "한국어",
  ru: "Русский",
  ur: "اردو",
};

/**
 * Shown until Google's script has loaded, and if it never does. Google's own
 * option list is the source of truth once available — its language codes are
 * not all ISO-639-1 (Hebrew is "iw", Javanese is "jw"), so they are read from
 * the widget rather than written down here.
 */
export const FALLBACK_LANGUAGES = Object.entries(NATIVE_LABELS).map(([code, label]) => ({
  code,
  label,
}));

const englishEntry = { code: PAGE_LANGUAGE, label: NATIVE_LABELS[PAGE_LANGUAGE] };

/** Every language Google currently offers, or null if the widget is not up yet. */
export const readWidgetLanguages = () => {
  const combo = document.querySelector(COMBO_SELECTOR);
  if (!combo || combo.options.length === 0) return null;

  const languages = Array.from(combo.options)
    .filter((option) => option.value)
    .map((option) => ({
      code: option.value,
      label: NATIVE_LABELS[option.value] ?? option.textContent.trim(),
    }));

  if (languages.length === 0) return null;

  // Google omits the page language from its options; it is the "off" state.
  return [englishEntry, ...languages];
};

/**
 * element.js loads asynchronously, so the option list does not exist on first
 * render. Polls until it does. Returns an unsubscribe function.
 */
export const onLanguagesReady = (callback, { intervalMs = 200, timeoutMs = 15000 } = {}) => {
  const immediate = readWidgetLanguages();
  if (immediate) {
    callback(immediate);
    return () => {};
  }

  const startedAt = Date.now();
  const timer = setInterval(() => {
    const languages = readWidgetLanguages();
    if (languages) {
      clearInterval(timer);
      callback(languages);
    } else if (Date.now() - startedAt > timeoutMs) {
      clearInterval(timer);
    }
  }, intervalMs);

  return () => clearInterval(timer);
};

/**
 * Every domain scope a googtrans cookie could be sitting on, widest last.
 *
 * Google's widget writes its own googtrans, and it picks a registrable parent
 * domain rather than the exact host. Clearing only the exact host leaves that
 * copy behind; on the next load the browser hands both to Google, the stale
 * one is read first, and the student is pinned to whatever language they
 * picked first no matter what they choose afterwards.
 *
 * Setting a cookie on a public suffix (".org", ".co.uk") is rejected by the
 * browser, so walking one label short of the TLD costs nothing when it
 * overshoots.
 */
export const domainVariants = (hostname) => {
  // A domain attribute is invalid for bare hosts such as "localhost".
  if (!hostname.includes(".")) return [null];

  const labels = hostname.split(".");
  const domains = [null];
  for (let i = 0; i < labels.length - 1; i++) {
    const domain = labels.slice(i).join(".");
    domains.push(domain, `.${domain}`);
  }
  return domains;
};

const cookieDomains = () => domainVariants(window.location.hostname);

const writeCookie = (value) => {
  // Clear every scope before writing, then write exactly one cookie. Writing
  // the value across all of them is what produced duplicate googtrans entries
  // that disagreed with each other.
  clearCookie();
  document.cookie = `${COOKIE_NAME}=${value}; path=/`;
};

const clearCookie = () => {
  cookieDomains().forEach((domain) => {
    const parts = [`${COOKIE_NAME}=`, "path=/", "expires=Thu, 01 Jan 1970 00:00:00 GMT"];
    if (domain) parts.push(`domain=${domain}`);
    document.cookie = parts.join("; ");
  });
};

const readCookie = () => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

// The full language set is read from the widget, so the cookie is validated by
// shape rather than against a list we hold: "es", "zh-CN", "haw".
const LANGUAGE_CODE = /^[a-z]{2,3}(-[A-Za-z]{2,4})?$/i;

/** The language Google is currently translating into, e.g. "es". */
export const getCurrentLanguage = () => {
  const cookie = readCookie();
  if (!cookie) return PAGE_LANGUAGE;

  // Cookie is "/en/es"; the target language is the final segment.
  const target = cookie.split("/").filter(Boolean).pop();
  return target && LANGUAGE_CODE.test(target) ? target : PAGE_LANGUAGE;
};

/**
 * Switching reloads rather than driving Google's hidden <select>, so the page
 * is translated during load instead of while React holds a mounted tree.
 * Redux state is persisted to localStorage, so the session survives.
 */
export const setLanguage = (code) => {
  if (code === PAGE_LANGUAGE) {
    clearCookie();
  } else {
    writeCookie(`/${PAGE_LANGUAGE}/${code}`);
  }
  window.location.reload();
};

/**
 * Google's widget translates the document once, shortly after load, and then
 * stops revisiting it. Anything React mounts later stays in the source
 * language: the career modal opens in English however long you wait, and on a
 * slow connection the cluster results can land after the pass and miss it too.
 *
 * Re-selecting the language that is already active is a no-op, so forcing a
 * fresh pass means routing through the page language and back. That costs a
 * brief flash of untranslated English, which is why this is called only when
 * new content has actually appeared rather than on a timer.
 *
 * Returns false when there is nothing to do — widget not up, or the student is
 * reading the page in its original language.
 */
export const retranslate = () => {
  const combo = document.querySelector(COMBO_SELECTOR);
  const target = getCurrentLanguage();
  if (!combo || target === PAGE_LANGUAGE) return false;

  combo.value = RESTORE_VALUE;
  combo.dispatchEvent(new Event("change"));

  // The widget needs a turn to tear the existing translation down; asking for
  // the target language in the same task leaves it showing the original text.
  setTimeout(() => {
    combo.value = target;
    combo.dispatchEvent(new Event("change"));
  }, RESTORE_DELAY_MS);

  return true;
};

export const initGoogleTranslate = () => {
  if (document.getElementById(SCRIPT_ID)) return;

  if (!document.getElementById(WIDGET_CONTAINER_ID)) {
    const container = document.createElement("div");
    container.id = WIDGET_CONTAINER_ID;
    document.body.appendChild(container);
  }

  // element.js invokes this global by name once it loads.
  window.googleTranslateElementInit = () => {
    if (!window.google?.translate?.TranslateElement) return;
    // No includedLanguages: offer every language Google supports.
    new window.google.translate.TranslateElement(
      {
        pageLanguage: PAGE_LANGUAGE,
        autoDisplay: false,
      },
      WIDGET_CONTAINER_ID
    );
  };

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.body.appendChild(script);
};
